import { forwardRef, type KeyboardEvent, type MouseEvent, memo, useCallback, useState } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import {
  DropdownMenu,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@air/primitive-dropdown-menu';

import { WorkspaceAgentMentionFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionFilterChip';
import { workspaceAgentMentionFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionFilterChipChrome';
import {
  type WorkspaceAgentBrandKitOption,
  type WorkspaceAgentMentionSourceKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';

const CHOOSE_BRAND_KIT_LABEL = 'Choose brand kit';
const CHOOSE_BRAND_KIT_LABEL_ID = 'workspace-agent-mention-choose-brand-kit-label';

export type WorkspaceAgentMentionBrandKitFilterChipProps = {
  brandKits: readonly WorkspaceAgentBrandKitOption[];
  isActive: boolean;
  /** The selected kit's name, which is what the chip shows once a kit has resolved. */
  label: string;
  onSelect: (sourceKey: WorkspaceAgentMentionSourceKey) => void;
  onSelectBrandKit: (brandKitId: string) => void;
  /** Where focus returns when the caret menu closes or Tab leaves the caret. */
  onRefocusEditor?: () => void;
  /** Shift+Tab off the caret steps back past the picker; the popup decides what that costs. */
  onShiftTab: () => void;
  selectedBrandKitId: string | null;
  sourceKey: WorkspaceAgentMentionSourceKey;
};

/**
 * Two buttons in one chip: the label switches the picker to brand kit rows, the caret picks which
 * kit those rows come from. The caret is the popup's only focusable control — a menu must own the
 * keyboard while it is open — so Shift+Tab from the composer lands here, Tab from here returns to
 * the host when one is provided, and Radix returns focus there when the menu closes. With
 * one kit there is nothing to choose, so the chip is plain.
 *
 * The kits are a single-select choice, not a list of commands, so they are radio items: which kit is
 * current has to be announced, and a check glyph is announced to nobody.
 */
export const WorkspaceAgentMentionBrandKitFilterChip = memo(
  forwardRef<HTMLButtonElement, WorkspaceAgentMentionBrandKitFilterChipProps>(
    (
      {
        brandKits,
        isActive,
        label,
        onSelect,
        onSelectBrandKit,
        onRefocusEditor,
        onShiftTab,
        selectedBrandKitId,
        sourceKey,
      },
      ref,
    ) => {
      const [isMenuOpen, setIsMenuOpen] = useState(false);
      const canPickKit = brandKits.length > 1;
      const ariaLabel = `Brand kit: ${label}`;

      /** A mousedown here would blur the editor. */
      const handleMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
      }, []);

      const handleCaretKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>) => {
          if (event.key !== 'Tab' || event.metaKey || event.ctrlKey || event.altKey) {
            return;
          }

          // The caret lives in a portal in front of the page. Browser tab order from here would
          // leave the still-open picker; send focus back to the host instead.
          if (event.shiftKey) {
            event.preventDefault();
            onShiftTab();
            return;
          }

          if (!onRefocusEditor) {
            return;
          }

          event.preventDefault();
          onRefocusEditor();
        },
        [onRefocusEditor, onShiftTab],
      );

      const handleCloseAutoFocus = useCallback(
        (event: Event) => {
          if (!onRefocusEditor) {
            return;
          }

          // Radix would focus the trigger; the picker is still open, so the host needs focus back.
          event.preventDefault();
          onRefocusEditor();
        },
        [onRefocusEditor],
      );

      if (!canPickKit) {
        return (
          <WorkspaceAgentMentionFilterChip
            ariaLabel={ariaLabel}
            isActive={isActive}
            label={label}
            onSelect={onSelect}
            sourceKey={sourceKey}
          />
        );
      }

      return (
        <span className="inline-flex min-w-0 shrink-0 items-center">
          <WorkspaceAgentMentionFilterChip
            ariaLabel={ariaLabel}
            className="rounded-r-none border-r-0"
            isActive={isActive}
            label={label}
            onSelect={onSelect}
            sourceKey={sourceKey}
          />
          <DropdownMenu
            onCloseAutoFocus={handleCloseAutoFocus}
            onOpenChange={setIsMenuOpen}
            open={isMenuOpen}
            size="small"
            trigger={
              <button
                aria-label={CHOOSE_BRAND_KIT_LABEL}
                className={workspaceAgentMentionFilterChip({ isCaret: true })}
                data-testid="WORKSPACE_AGENT_MENTION_BRAND_KIT_CHIP_CARET"
                onKeyDown={handleCaretKeyDown}
                onMouseDown={handleMouseDown}
                ref={ref}
                type="button"
              >
                <ChevronDownIcon aria-hidden size="xs" />
              </button>
            }
          >
            <DropdownMenuLabel id={CHOOSE_BRAND_KIT_LABEL_ID}>{CHOOSE_BRAND_KIT_LABEL}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              aria-labelledby={CHOOSE_BRAND_KIT_LABEL_ID}
              onValueChange={onSelectBrandKit}
              value={selectedBrandKitId ?? undefined}
            >
              {brandKits.map((kit) => (
                <DropdownMenuRadioItem
                  data-brand-kit-id={kit.id}
                  data-testid="WORKSPACE_AGENT_MENTION_BRAND_KIT_MENU_ITEM"
                  key={kit.id}
                  label={kit.name}
                  size="small"
                  value={kit.id}
                />
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenu>
        </span>
      );
    },
  ),
);

WorkspaceAgentMentionBrandKitFilterChip.displayName = 'WorkspaceAgentMentionBrandKitFilterChip';
